<template>
  <main class="app-content">
    <div class="app-title">
      <div>
        <h1><i class="fa fa-credit-card"></i> AI Agent — Tổng quan Billing</h1>
        <p>Theo dõi cross-client: phân bố gói, top tiêu, blocked, anomaly, health check.</p>
      </div>
      <ul class="app-breadcrumb breadcrumb">
        <li class="breadcrumb-item"><i class="fa fa-home fa-lg"></i></li>
        <li class="breadcrumb-item"><router-link to="/ai-agent-overview">AI Agent Billing</router-link></li>
      </ul>
    </div>

    <!-- Filter days + actions -->
    <div class="tile mb-3">
      <b-form-radio-group v-model="days" :options="dayOptions" buttons button-variant="outline-primary" size="sm" @change="loadAll" />
      <b-button size="sm" variant="primary" class="ml-2" @click="loadAll"><i class="fa fa-refresh"></i> Tải lại</b-button>
      <b-button size="sm" variant="success" class="ml-2" @click="exportBilling"><i class="fa fa-download"></i> Export CSV kỳ này</b-button>
    </div>

    <!-- System total cards: 2 row (Token/Doanh thu/Chi phí/Lãi-lỗ + Hội thoại/Margin/Block/Lỗi) -->
    <div class="row mb-3" v-if="overview.system">
      <div class="col-md-3">
        <div class="card border-info">
          <div class="card-body">
            <div class="text-muted small">Tổng token ({{ days }} ngày)</div>
            <h3>{{ (overview.system.totalTokens || 0).toLocaleString() }}</h3>
            <small class="text-muted">{{ overview.system.conversations || 0 }} hội thoại</small>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card border-success">
          <div class="card-body">
            <div class="text-muted small">Doanh thu ước tính</div>
            <h3 class="text-success">{{ (overview.system.estimatedRevenueVnd || 0).toLocaleString() }} đ</h3>
            <small class="text-muted">
              <span v-if="overview.flatPricePer1M">{{ overview.flatPricePer1M.toLocaleString() }}đ/1M (flat)</span>
              <span v-else class="text-warning"><i class="fa fa-exclamation-triangle"></i> Chưa cấu hình giá Etc</span>
              <span v-if="overview.aiProvider" class="ml-2">· {{ overview.aiProvider }}</span>
            </small>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card border-warning">
          <div class="card-body">
            <div class="text-muted small">Chi phí thực ({{ overview.aiProvider }})</div>
            <h3 class="text-warning">{{ (overview.system.costVnd || 0).toLocaleString() }} đ</h3>
            <small class="text-muted">Mua từ {{ overview.aiProvider }} theo giá vốn snapshot</small>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card" :class="marginCardClass">
          <div class="card-body">
            <div class="text-muted small">
              <b>{{ overview.system.marginVnd >= 0 ? 'LÃI' : 'LỖ' }}</b>
              ({{ overview.system.marginPct }}%)
            </div>
            <h3 :class="marginTextClass">
              {{ Math.abs(overview.system.marginVnd || 0).toLocaleString() }} đ
            </h3>
            <small :class="marginTextClass">
              <i class="fa" :class="overview.system.marginVnd >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'"></i>
              Margin {{ overview.system.marginPct }}%
            </small>
          </div>
        </div>
      </div>
    </div>
    <!-- Row phụ: thông tin secondary -->
    <div class="row mb-3" v-if="overview.system">
      <div class="col-md-3">
        <div class="card" :class="overview.system.blockedQuota > 0 ? 'border-danger' : 'border-secondary'">
          <div class="card-body">
            <div class="text-muted small">Bị chặn do hết quota</div>
            <h4 :class="overview.system.blockedQuota > 0 ? 'text-danger' : ''">{{ (overview.system.blockedQuota || 0).toLocaleString() }}</h4>
            <small class="text-muted">Lỗi: {{ overview.system.errorCount || 0 }}</small>
          </div>
        </div>
      </div>
    </div>

    <!-- Phân bố gói AI Token v2 (chốt 2026-06-05) -->
    <div class="row mb-3">
      <div class="col-md-6">
        <div class="card">
          <div class="card-header"><b>Phân bố Gói AI Token tháng</b></div>
          <div class="card-body">
            <table class="table table-sm mb-0">
              <tr>
                <td><span class="badge badge-secondary">Không gói (per-token)</span></td>
                <td class="text-right">{{ (overview.packageDistribution || {}).none || 0 }} client</td>
              </tr>
              <tr>
                <td><span class="badge badge-info">Basic (200k / 1M)</span></td>
                <td class="text-right">{{ (overview.packageDistribution || {}).basic || 0 }} client</td>
              </tr>
              <tr>
                <td><span class="badge badge-primary">Starter (1.9M / 10M)</span></td>
                <td class="text-right">{{ (overview.packageDistribution || {}).starter || 0 }} client</td>
              </tr>
              <tr>
                <td><span class="badge badge-warning">Pro (3.6M / 20M)</span></td>
                <td class="text-right">{{ (overview.packageDistribution || {}).pro || 0 }} client</td>
              </tr>
              <tr>
                <td><span class="badge badge-success">Ultimate (7.65M / 45M)</span></td>
                <td class="text-right">{{ (overview.packageDistribution || {}).ultimate || 0 }} client</td>
              </tr>
            </table>
          </div>
        </div>
      </div>

      <!-- Health check -->
      <div class="col-md-6">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <b>Health check API keys</b>
            <b-button size="sm" variant="outline-primary" @click="loadHealth" :disabled="healthLoading">
              <i class="fa fa-refresh" :class="{ 'fa-spin': healthLoading }"></i> Kiểm tra
            </b-button>
          </div>
          <div class="card-body">
            <table class="table table-sm mb-0">
              <tr v-for="(v, k) in healthRows" :key="k">
                <td>{{ healthLabels[k] || k }}</td>
                <td class="text-right">
                  <span v-if="v && v.ok" class="text-success">
                    <i class="fa fa-check-circle"></i> OK
                    <small v-if="v.latencyMs" class="text-muted">({{ v.latencyMs }}ms)</small>
                  </span>
                  <span v-else class="text-danger">
                    <i class="fa fa-times-circle"></i> {{ (v && v.error) || 'Chưa test' }}
                  </span>
                </td>
              </tr>
              <tr v-if="health.errorRate1h">
                <td>Tỉ lệ lỗi 1 giờ qua</td>
                <td class="text-right">
                  <span :class="health.errorRate1h.rate >= 20 ? 'text-danger' : 'text-muted'">
                    {{ health.errorRate1h.rate }}% ({{ health.errorRate1h.error }}/{{ health.errorRate1h.total }})
                  </span>
                </td>
              </tr>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Anomaly -->
    <div class="card mb-3" v-if="anomalies.length">
      <div class="card-header bg-warning">
        <b><i class="fa fa-exclamation-triangle"></i> Anomaly — Client tiêu token bất thường hôm nay (> 3× median 7 ngày)</b>
      </div>
      <b-table small striped :items="anomalies" :fields="anomalyFields">
        <template #cell(clientName)="r">
          <a :href="`/ai-agent-logs?clientId=${r.item.clientId}`">{{ r.item.clientName || '(deleted)' }}</a>
          <small class="text-muted ml-2">{{ r.item.domain }}</small>
        </template>
        <template #cell(billingMode)="r">
          <span v-if="r.item.aiAgentPackage === 'ultimate'" class="badge badge-success">Ultimate</span>
          <span v-else-if="r.item.aiAgentPackage === 'pro'" class="badge badge-warning">Pro</span>
          <span v-else-if="r.item.aiAgentPackage === 'starter'" class="badge badge-primary">Starter</span>
          <span v-else-if="r.item.aiAgentPackage === 'basic'" class="badge badge-info">Basic</span>
          <span v-else-if="r.item.billingMode === 'postpaid'" class="badge badge-secondary">Trả sau</span>
          <span v-else-if="r.item.billingMode === 'prepaid'" class="badge badge-info">Trả trước</span>
          <span v-else class="badge badge-secondary">Chưa chọn</span>
        </template>
        <template #cell(ratio)="r">
          <b class="text-danger" v-if="r.item.ratio">{{ r.item.ratio }}×</b>
          <span v-else class="text-muted">N/A</span>
        </template>
      </b-table>
    </div>

    <!-- Top clients -->
    <div class="card mb-3">
      <div class="card-header"><b>Top 20 clients theo token usage ({{ days }} ngày)</b></div>
      <b-table small striped hover :items="overview.topClients || []" :fields="clientFields">
        <template #cell(name)="r">
          <a :href="`/ai-agent-logs?clientId=${r.item._id}`">{{ r.item.name || '(deleted)' }}</a>
          <small class="text-muted ml-2">{{ r.item.domain }}</small>
          <span v-if="r.item.hasRisk" class="badge badge-danger ml-2"><i class="fa fa-warning"></i> Risk</span>
        </template>
        <template #cell(billingMode)="r">
          <span v-if="r.item.aiAgentPackage === 'ultimate'" class="badge badge-success">Ultimate</span>
          <span v-else-if="r.item.aiAgentPackage === 'pro'" class="badge badge-warning">Pro</span>
          <span v-else-if="r.item.aiAgentPackage === 'starter'" class="badge badge-primary">Starter</span>
          <span v-else-if="r.item.aiAgentPackage === 'basic'" class="badge badge-info">Basic</span>
          <span v-else-if="r.item.billingMode === 'postpaid'" class="badge badge-secondary">Trả sau</span>
          <span v-else-if="r.item.billingMode === 'prepaid'" class="badge badge-info">Trả trước</span>
          <span v-else class="badge badge-secondary">Chưa chọn</span>
        </template>
        <template #cell(totalTokens)="r">{{ (r.item.totalTokens || 0).toLocaleString() }}</template>
        <template #cell(estimatedRevenueVnd)="r">
          <b class="text-success">{{ (r.item.estimatedRevenueVnd || 0).toLocaleString() }} đ</b>
        </template>
        <template #cell(costVnd)="r">
          <span class="text-warning">{{ (r.item.costVnd || 0).toLocaleString() }} đ</span>
        </template>
        <template #cell(marginVnd)="r">
          <b :class="r.item.marginVnd >= 0 ? 'text-success' : 'text-danger'">
            <i class="fa" :class="r.item.marginVnd >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'"></i>
            {{ Math.abs(r.item.marginVnd || 0).toLocaleString() }} đ ({{ r.item.marginPct }}%)
          </b>
        </template>
        <template #cell(errorRate)="r">
          <span :class="r.item.errorRate >= 20 ? 'text-danger' : (r.item.errorRate >= 10 ? 'text-warning' : 'text-muted')">
            {{ r.item.errorRate }}%
          </span>
        </template>
        <template #cell(blockedQuota)="r">
          <span v-if="r.item.blockedQuota > 0" class="badge badge-danger">{{ r.item.blockedQuota }}</span>
          <span v-else class="text-muted">0</span>
        </template>
      </b-table>
    </div>

    <!-- Blocked recent -->
    <div class="card mb-3" v-if="(overview.blockedRecent || []).length">
      <div class="card-header bg-light"><b>Client bị chặn do hết quota (gần nhất 30)</b></div>
      <b-table small striped :items="overview.blockedRecent" :fields="blockedFields">
        <template #cell(name)="r">
          <a :href="`/ai-agent-logs?clientId=${r.item._id}`">{{ r.item.name || '(deleted)' }}</a>
          <small class="text-muted ml-2">{{ r.item.domain }}</small>
        </template>
        <template #cell(billingMode)="r">
          <span v-if="r.item.aiAgentPackage === 'ultimate'" class="badge badge-success">Ultimate</span>
          <span v-else-if="r.item.aiAgentPackage === 'pro'" class="badge badge-warning">Pro</span>
          <span v-else-if="r.item.aiAgentPackage === 'starter'" class="badge badge-primary">Starter</span>
          <span v-else-if="r.item.aiAgentPackage === 'basic'" class="badge badge-info">Basic</span>
          <span v-else-if="r.item.billingMode === 'postpaid'" class="badge badge-secondary">Trả sau</span>
          <span v-else-if="r.item.billingMode === 'prepaid'" class="badge badge-info">Trả trước</span>
          <span v-else class="badge badge-secondary">Chưa chọn</span>
        </template>
        <template #cell(lastBlock)="r">{{ formatTime(r.item.lastBlock) }}</template>
        <template #cell(creditBalance)="r">{{ (r.item.creditBalance || 0).toLocaleString() }} đ</template>
      </b-table>
    </div>
  </main>
</template>

<script>
export default {
  data() {
    return {
      days: 30,
      dayOptions: [
        { text: '7 ngày', value: 7 },
        { text: '30 ngày', value: 30 },
        { text: '90 ngày', value: 90 },
        { text: '180 ngày', value: 180 },
        { text: '365 ngày', value: 365 },
      ],
      overview: {},
      anomalies: [],
      health: {},
      healthLoading: false,
      healthLabels: {
        openai: 'OpenAI (GPT-4o)',
        customEmbed: 'Custom Embed (bgem3)',
        fptCloud: 'FPT Cloud',
        huggingFace: 'HuggingFace',
      },
      clientFields: [
        { key: 'name', label: 'Client' },
        { key: 'billingMode', label: 'Chế độ', class: 'text-center' },
        { key: 'conversations', label: 'Hội thoại', class: 'text-right' },
        { key: 'totalTokens', label: 'Tokens', class: 'text-right' },
        { key: 'estimatedRevenueVnd', label: 'Doanh thu', class: 'text-right' },
        { key: 'costVnd', label: 'Chi phí thực', class: 'text-right' },
        { key: 'marginVnd', label: 'Lãi / Lỗ', class: 'text-right' },
        { key: 'errorRate', label: '% lỗi', class: 'text-right' },
        { key: 'blockedQuota', label: 'Blocked', class: 'text-center' },
      ],
      anomalyFields: [
        { key: 'clientName', label: 'Client' },
        { key: 'billingMode', label: 'Chế độ', class: 'text-center' },
        { key: 'todayTokens', label: 'Token hôm nay', class: 'text-right', formatter: v => (v || 0).toLocaleString() },
        { key: 'medianTokens7d', label: 'Median 7 ngày', class: 'text-right', formatter: v => (v || 0).toLocaleString() },
        { key: 'ratio', label: 'Bội số', class: 'text-center' },
        { key: 'todayCount', label: 'Hội thoại', class: 'text-right' },
      ],
      blockedFields: [
        { key: 'name', label: 'Client' },
        { key: 'billingMode', label: 'Chế độ', class: 'text-center' },
        { key: 'count', label: 'Số lần block', class: 'text-right' },
        { key: 'creditBalance', label: 'Số dư', class: 'text-right' },
        { key: 'lastBlock', label: 'Block gần nhất', class: 'text-right' },
      ],
    };
  },
  computed: {
    healthRows() {
      const { openai, customEmbed, fptCloud, huggingFace } = this.health;
      return { openai, customEmbed, fptCloud, huggingFace };
    },
    // Card lãi/lỗ: viền + chữ màu theo dấu margin
    marginCardClass() {
      const m = (this.overview.system && this.overview.system.marginVnd) || 0;
      if (m > 0) return 'border-success';
      if (m < 0) return 'border-danger';
      return 'border-secondary';
    },
    marginTextClass() {
      const m = (this.overview.system && this.overview.system.marginVnd) || 0;
      if (m > 0) return 'text-success';
      if (m < 0) return 'text-danger';
      return '';
    },
  },
  methods: {
    loadAll() {
      this.loadOverview();
      this.loadAnomaly();
    },
    loadOverview() {
      this.$service.get('/ai-agent/billing-overview', { params: { days: this.days } })
        .then(r => { this.overview = r.data; })
        // eslint-disable-next-line no-console
        .catch(e => console.error('Load overview fail', e));
    },
    loadAnomaly() {
      this.$service.get('/ai-agent/anomaly')
        .then(r => { this.anomalies = (r.data && r.data.anomalies) || []; })
        // eslint-disable-next-line no-console
        .catch(e => console.error('Load anomaly fail', e));
    },
    loadHealth() {
      this.healthLoading = true;
      this.$service.get('/ai-agent/openai-health')
        .then(r => { this.health = r.data; })
        // eslint-disable-next-line no-console
        .catch(e => console.error('Load health fail', e))
        .finally(() => { this.healthLoading = false; });
    },
    exportBilling() {
      const period = new Date().toISOString().slice(0, 7);
      this.$service.get('/ai-agent/export-billing', { params: { period }, responseType: 'blob' })
        .then(res => {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const a = document.createElement('a');
          a.href = url;
          a.download = `ai-agent-billing-${period}.csv`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        })
        // eslint-disable-next-line no-console
        .catch(e => console.error('Export fail', e));
    },
    formatTime(ts) {
      if (!ts) return '';
      return new Date(ts).toLocaleString('vi-VN');
    },
  },
  mounted() {
    this.loadAll();
    this.loadHealth();
  },
};
</script>
